import { useCallback, useEffect, useRef } from "react";

/** How long a control keeps its "just activated" ring, as in the prototype. */
export const ACTIVATION_FLASH_MS = 900;

const ACTIVATED_CLASS = "button-activated";

/**
 * Briefly rings a control after it is pressed, giving visible confirmation that
 * an action fired even when nothing else on screen changes.
 *
 * The class is applied to the DOM node directly rather than through state: the
 * flash is presentation only, and routing it through React would re-render the
 * whole panel twice per click.
 */
export function useActivationFlash() {
  // One timer per element, so a rapid second press restarts its own ring
  // instead of being cut short by the previous press's timer.
  const timers = useRef(new Map<HTMLElement, ReturnType<typeof setTimeout>>());

  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
      timers.current.clear();
    },
    [],
  );

  return useCallback((element: HTMLElement | null | undefined) => {
    if (!element) return;

    const pending = timers.current.get(element);
    if (pending) clearTimeout(pending);

    // Remove first so the animation restarts rather than continuing.
    element.classList.remove(ACTIVATED_CLASS);
    requestAnimationFrame(() => element.classList.add(ACTIVATED_CLASS));

    timers.current.set(
      element,
      setTimeout(() => {
        element.classList.remove(ACTIVATED_CLASS);
        timers.current.delete(element);
      }, ACTIVATION_FLASH_MS),
    );
  }, []);
}
